import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Table } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../Common/PageHeader/PageHeader';
import { getHirersFull } from '../../services/adminApi';
import { updateDocumentTitle } from '../../utils/documentTitle';
import './AdminHubPage.css';

/**
 * AdminHirerAccountsPage: /admin/hirers (Nitish, Sep 16 2026, V0 of the admin hub). The Hirer
 * Accounts board read INSIDE the app, on /admin/hirers/full (CEL-1673, hourly beat, cache-only,
 * server-side exclusions since Sep 1 2026). One row per external owner who hired: employees
 * (role, business, status, working sessions), paid credits, Stripe net dollars when the
 * fail-soft `stripe_dollars` block carries the email. Read-only; the HTML board
 * (dashboard/hirer-accounts) stays until Nitish has read this one for a week.
 */
const etTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET'; } catch (e) { return ''; }
};
const ageText = (d) => {
  let secs = null;
  if (d.cache && typeof d.cache.age_seconds === 'number') secs = d.cache.age_seconds;
  else if (d.computed_at) secs = Math.max(0, (Date.now() - new Date(d.computed_at).getTime()) / 1000);
  if (secs === null || Number.isNaN(secs)) return 'fresh';
  const mins = Math.round(secs / 60);
  return mins < 1 ? 'computed just now' : `computed ${mins} min ago (${etTime(d.computed_at || (d.cache && d.cache.computed_at))})`;
};
const FLAG_LABEL = { red: 'Lapsing', green: 'Paying', amber: 'Never paid' };
const FLAG_ORDER = { red: 0, green: 1, amber: 2 };
/* The Stripe block is fail-soft and its per-email shape is not pinned; resolve defensively. */
const dollarsFor = (block, email) => {
  if (!block || !email) return null;
  const maps = [block.by_email, block.emails, block.dollars, block];
  for (const m of maps) {
    if (m && typeof m === 'object' && !Array.isArray(m) && m[email] !== undefined) {
      const v = m[email];
      if (typeof v === 'number') return v;
      if (v && typeof v.net_usd === 'number') return v.net_usd;
      if (v && typeof v.paid_usd === 'number') return v.paid_usd;
    }
  }
  if (Array.isArray(block.rows)) {
    const r = block.rows.find((x) => x.email === email || x.owner_email === email);
    if (r && typeof (r.net_usd ?? r.paid_usd) === 'number') return r.net_usd ?? r.paid_usd;
  }
  return null;
};

const AdminHirerAccountsPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setBusy(true); setError(null);
    try { const { data: d } = await getHirersFull(); setData(d); }
    catch (e) { setError(String(e?.response?.data?.detail || e?.message || 'unavailable')); }
    setBusy(false);
  }, []);
  useEffect(() => { updateDocumentTitle('Hirer Accounts · Admin'); load(); }, [load]);

  const rows = useMemo(() => {
    if (!data || !Array.isArray(data.accounts)) return [];
    const stripe = data.stripe_dollars && data.stripe_dollars.status !== 'pending' ? data.stripe_dollars : null;
    return data.accounts.map((a) => {
      const employees = (a.employees || []).filter((e) => !e.deleted).map((e) => ({
        name: e.name || '(unnamed)', title: e.title || '(no title)', business: e.business || '',
        status: e.status || 'active', shifts: e.shifts || 0, hired: (e.hired_at_et || '').slice(0, 10),
      }));
      const hires = employees.map((e) => e.hired).filter(Boolean).sort();
      const credits = (a.paid && a.paid.total_paid_credits) || 0;
      const lastPaid = (a.paid && a.paid.last_paid) || '';
      // Health flag, same three groups as the HTML board (Sep 22 2026, Nitish): paid in the last 30 days =
      // paying, paid before that = lapsing, never paid = hired-never-paid. Source here is the credit ledger
      // (paid credits + last paid date); the HTML board reads Stripe dollars.
      const days = lastPaid ? Math.floor((Date.now() - new Date(`${lastPaid}T12:00:00Z`).getTime()) / 86400000) : null;
      const health = credits > 0 ? (days !== null && days <= 30 ? 'green' : 'red') : 'amber';
      return {
        key: a.owner_email, owner: a.owner_name || (a.owner_email || '').split('@')[0], email: a.owner_email,
        employees, firstHire: hires[0] || '', latestHire: hires[hires.length - 1] || '', credits, usd: dollarsFor(stripe, a.owner_email),
        health, lastPaid, lastPaidDays: days,
        balance: typeof a.balance === 'number' ? a.balance : null,
        msgs: typeof a.user_messages === 'number' ? a.user_messages : null,
      };
    }).filter((r) => r.employees.length > 0);
  }, [data]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => `${r.owner} ${r.email} ${r.employees.map((e) => `${e.name} ${e.title} ${e.business}`).join(' ')}`.toLowerCase().includes(s));
  }, [rows, q]);

  const hasUsd = rows.some((r) => typeof r.usd === 'number');
  const totalEmployees = rows.reduce((s, r) => s + r.employees.length, 0);
  const columns = [
    { title: 'Owner', dataIndex: 'owner', key: 'owner', render: (v, r) => (<span className="adm-emp"><b>{v}</b><span>{r.email}</span></span>), sorter: (a, b) => a.owner.localeCompare(b.owner) },
    { title: 'AI employees', key: 'emps', render: (_, r) => (
      <span className="adm-emp">{r.employees.map((e) => (
        <span key={`${e.name}-${e.hired}`}><b>{e.name}</b> · {e.title}{e.business ? ` · ${e.business}` : ''} · {e.status} · {e.shifts} session{e.shifts === 1 ? '' : 's'}{e.hired ? ` · hired ${e.hired}` : ''}</span>
      ))}</span>), sorter: (a, b) => a.employees.length - b.employees.length },
    { title: 'Flag', dataIndex: 'health', key: 'health', width: 110,
      render: (v) => (<span className={`adm-flag ${v}`}><i />{FLAG_LABEL[v]}</span>),
      filters: Object.keys(FLAG_LABEL).map((k) => ({ text: FLAG_LABEL[k], value: k })), onFilter: (val, r) => r.health === val,
      sorter: (a, b) => FLAG_ORDER[a.health] - FLAG_ORDER[b.health] },
    ...(hasUsd ? [{ title: 'Stripe net $', dataIndex: 'usd', key: 'usd', width: 110, align: 'right', render: (v) => (typeof v === 'number' ? `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'), sorter: (a, b) => (a.usd || 0) - (b.usd || 0) }] : []),
    { title: 'Paid credits', dataIndex: 'credits', key: 'credits', width: 110, align: 'right', render: (v) => (v ? v.toLocaleString() : '—'), sorter: (a, b) => a.credits - b.credits },
    { title: 'Balance', dataIndex: 'balance', key: 'balance', width: 100, align: 'right',
      render: (v) => (v === null ? '—' : <span className={v < 0 ? 'adm-neg' : ''}>{v.toLocaleString()}</span>), sorter: (a, b) => (a.balance || 0) - (b.balance || 0) },
    { title: 'Msgs', dataIndex: 'msgs', key: 'msgs', width: 80, align: 'right', render: (v) => (v === null ? '—' : v.toLocaleString()), sorter: (a, b) => (a.msgs || 0) - (b.msgs || 0) },
    { title: 'Last paid', dataIndex: 'lastPaid', key: 'lastPaid', width: 130,
      render: (v, r) => (v ? <span>{v} <span className="adm-muted">({r.lastPaidDays}d)</span></span> : '—'), sorter: (a, b) => (a.lastPaid < b.lastPaid ? -1 : 1) },
    { title: 'Latest hire', dataIndex: 'latestHire', key: 'latestHire', width: 110, sorter: (a, b) => (a.latestHire < b.latestHire ? -1 : 1), defaultSortOrder: 'descend' },
  ];

  return (
    <div className="adm-page">
      <p className="adm-crumb"><Link to="/admin">Admin</Link> › Hirer Accounts</p>
      <PageHeader
        title="Hirer Accounts"
        subtitle="Every external owner who hired an AI employee: who they hired, what it runs, what they paid."
        actions={<Button icon={<ReloadOutlined />} onClick={load} loading={busy}>Refresh</Button>}
      />
      <p className="adm-stamp">
        {data ? <span>{rows.length} owners · {totalEmployees} employees · hourly cache · {ageText(data)} · locked and internal accounts excluded server-side{hasUsd ? '' : ' · Stripe dollars pending'}</span> : <span>Loading…</span>}
        <Input allowClear placeholder="Filter by owner, email, employee, business" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 360 }} />
      </p>
      {error && !data ? <div className="adm-empty">The live cache is not serving: {error}</div> : null}
      {/* scroll.x: on a phone the three columns overflow the page with nothing to scroll them (Nitish, Sep 18 2026);
          the desktop column is wider than 720 so it never shows a bar there. */}
      {data ? <Table className="adm-table" size="small" columns={columns} dataSource={filtered} pagination={{ pageSize: 25, showSizeChanger: false }} loading={busy} scroll={{ x: 1180 }} /> : null}
    </div>
  );
};

export default AdminHirerAccountsPage;
